import React, { useState, useMemo } from 'react';
import { DataTable } from 'Components/DataTable';
import { Column } from 'react-table';
import { useGetPayoutRequestsQuery } from 'services';
import { Box, useMediaQuery, VStack, theme, Flex, Text, Image, Button, useDisclosure, Heading, Stack, Divider, Menu, MenuButton, MenuItem, MenuList, Center } from '@chakra-ui/react';
import WithdrawalIcon from 'Assets/svg/WithdrawalIcon.svg'; 
import { DashboardSettingLayout } from 'Layouts';
import { CustomModal } from 'Components';
import { IHistory } from 'Types/Interface/IHistory';
import { formatDateShort, TransactionStatusComponent } from 'Utils/dataTableUtils';
import { useLocale } from 'context/LocalizationContext';
import { TransactionStatus } from 'Types/Enums';
import { BsFilter } from 'react-icons/bs';

export const AllWithdrawalHistory = () => {
	const { locale } = useLocale();
	const [isMobile] = useMediaQuery(`(max-width: ${theme.breakpoints.lg})`);
	const params = {pageNumber: 1, pageSize: 1000, locale };
	const { data, isLoading, isFetching, isError } = useGetPayoutRequestsQuery(params);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const newData = data?.data?.map((item) => ({
		...item,
		id: item.id,
	}));
	const [selectedRow, setSelectedRow] = useState<IHistory>();
	const [statusFilter, setStatusFilter] = useState<string>('all');

	const handleClick = (data:IHistory) => {
		setSelectedRow(data);
		onOpen();
	};

	const filteredData = useMemo(() => {
		if (statusFilter === 'all' || !newData) {
			return newData;
		}
		const filterValue = Number(statusFilter);
		return newData.filter(item => item.status === filterValue);
	}, [newData, statusFilter]);
	
	const columns: Column<IHistory>[] = React.useMemo(
		() => [
			{
				Header: 'Amount',
				accessor: 'points',
				Cell: (item) => (
					<Box color={'brand.black'} fontWeight={400} fontSize={'14px'} textAlign={'left'}>
						{item.row.original.points} LNT
					</Box>
				),
			},
			{
				Header: 'Date',
				accessor: 'dateCreated',
				Cell: (item) => (
					<Box color={'brand.black'} fontWeight={400} fontSize={'14px'}>
						{formatDateShort(item.row.original.dateCreated)}
					</Box>
				),
			},
			{
				id: 'status',
				Header: 'Status',
				accessor: 'status',
				Cell: (item) => <TransactionStatusComponent status={item.row.original.status} />,
			},
			{
				Header: 'Action',
				Cell: (
					<Button
						fontSize={'14px'}
						fontWeight={400}
						color={'brand.black'}
						w={'70px'}
						h={'31px'}
						borderRadius={'56px'}
						border={'1px solid #586068'}
					>
						{'View'}
					</Button>
				),
			},
		],
		[],
	);

	const TransactionReceipt = () => {
		return(
			<Stack spacing={'30px'} mt={['80px', '0px']}  p={[6,10]}>
				<Heading textAlign={'left'} fontSize={['18px','22px']}>Transaction Receipt</Heading>
				<Divider border={'1px solid #121B26'} />
				<Stack spacing={'30px'}>
					<Flex justifyContent={'space-between'}>
						<Text fontSize={['14px','16px']} color={'brand.black'}>Date:</Text>
						<Text fontSize={['14px','16px']} color={'brand.black'}>{formatDateShort(selectedRow?.dateCreated ?? '')}</Text>
					</Flex>
					<Flex justifyContent={'space-between'}>
						<Text fontSize={['14px','16px']} color={'brand.black'}>Amount:</Text>
						<Text fontSize={['14px','16px']} color={'brand.black'}>{selectedRow?.points} LNT</Text>
					</Flex>
					<Flex justifyContent={'space-between'}>
						<Text fontSize={['14px','16px']} color={'brand.black'}>Transaction ID:</Text>
						<Text w={'50%'} textAlign={'right'} fontSize={['14px','16px']} color={'brand.black'}>{selectedRow?.referenceId}</Text>
					</Flex>
					<Flex justifyContent={'space-between'} pb={['70px', '0px']} >
						<Text fontSize={['14px','16px']} color={'brand.black'}>Status:</Text>
						<TransactionStatusComponent status={selectedRow?.status} />
					</Flex>
				</Stack>

				{isMobile && <Button  h={['50px']} variant={'primary'} isLoading={isLoading} onClick={onClose} borderRadius={'5px'}>
					Done
				</Button>}
			</Stack>
		);
	};

	const mobileColumns: Column<IHistory>[] = React.useMemo(
		() => [
			{
				accessor: 'dateCreated',
				Cell: (item) => {
					return (
						<Flex alignItems={'center'}>
							<Image w={'40px'} height={'40px'} src={WithdrawalIcon} mr={'10px'} />
							<VStack align={'flex-start'} spacing={'5px'}>
								<Box fontSize='14px'>Payment</Box>
								<Box fontSize='12px' color='loopGrey.800'>
									{formatDateShort(item.row.original.dateCreated)}
								</Box>
							</VStack>
						</Flex>
					);
				},
			},
			{
				accessor: 'points',
				Cell: (item) => (
					<VStack alignItems='flex-end'>
						<Box fontWeight='400' fontSize={'14px'}>
							{item.row.original.points} LNT
						</Box>
						<TransactionStatusComponent status={item.row.original.status} />
					</VStack>
				),
			},
		],
		[],
	);

	return (
		<DashboardSettingLayout>
			<Text fontSize={['14px', '16px']} fontWeight={400} color={'brand.black'} pb={'20px'} w={['auto','980px']}>
                Here you will find a comprehensive list of your withdrawal history thus far on Loop. Should you have any questions, please feel free to contact us at admin@feedtheloop.com
			</Text>
			<Flex gap={2} mb={4} alignItems={'center'} w={['auto','980px']}>
				<Menu>
					{!isMobile ? (
						<MenuButton
							as={Button}
							rightIcon={<BsFilter />}
							_focus={{ outline: 'none' }}
							border={'1px solid #C1C8CF'}
							borderRadius={'10px'}
							px={4}
							py={3}
							fontSize="12px"
							color={'#121B26' }
							bgColor="white"
						>
							{statusFilter === 'all' ? ' Status' : 
								statusFilter === String(TransactionStatus.Pending) ? 'Pending' :
								statusFilter === String(TransactionStatus.Completed) ? 'Completed' :
								statusFilter === String(TransactionStatus.Failed) ? 'Failed' :
								statusFilter === String(TransactionStatus.Canceled) ? 'Canceled' : 'Filter by Status'
							}
						</MenuButton>
					) : (
						<MenuButton
							as={Button}
							_focus={{ outline: 'none' }}
							border={'1px solid #C1C8CF'}
							borderRadius={'5px'}
							px={2}
							py={2}
							bgColor={statusFilter !== 'all' ? '#FF6F61' : 'white'}
							color={statusFilter !== 'all' ? 'white' : '#121B26'}
						>
							<BsFilter fontSize='1.4em' />
						</MenuButton>
					)}
					<MenuList>
						<MenuItem onClick={() => setStatusFilter('all')}>All</MenuItem>
						<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Pending))}>Pending</MenuItem>
						<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Completed))}>Completed</MenuItem>
						<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Failed))}>Failed</MenuItem>
						<MenuItem onClick={() => setStatusFilter(String(TransactionStatus.Canceled))}>Canceled</MenuItem>
					</MenuList>
				</Menu>
			</Flex>
			{filteredData && filteredData.length === 0 && !isLoading && !isFetching ? (
				<Center 
					border="1px solid" 
					borderColor="#ECEFF2" 
					borderRadius="10px" 
					p={10}
					minH="200px"
					flexDirection="column"
				>
					<Text fontSize="16px" color="loopGrey.500" mb={2}>
						No transactions found
					</Text>
					<Text fontSize="14px" color="loopGrey.400">
						{statusFilter !== 'all' ? 'Try changing the filter or select "All" to see all transactions' : 'No withdrawal transactions yet'}
					</Text>
				</Center>
			) : (
				<DataTable
					data={filteredData || []}
					columns={isMobile ? mobileColumns : columns}
					isLoading={isLoading || isFetching}
					isError={isError}
					isMobile={isMobile}
					showCheckboxes={false}
					showMenu={false}
					onclickRow={handleClick}
				/>
			)}
			<CustomModal content={<TransactionReceipt />} isOpen={isOpen}
				onClose={onClose} modalContentStyles={{borderRadius : 10, h:392,  w: 457}}  size={isMobile ? 'full' : 'md'}
			/>
		</DashboardSettingLayout>
	);
};
